import { RootPage } from '../page';
import $api from '../../api';
import { FeedbackModel } from '../../models/Gen/BaseModels';
import IntegrateSlackButton from '../../components/integrateSlackButton';
import DisconnectSlackButton from '../../components/disconnectSlackButton';
import UrlFilterDescriptor from '../../components/descriptors/urlFilterDescriptor';
import languages from '../../utils/languages';
import * as siteConfig from '../../site-config/trackableConfig';
import getHashOrDefault from '../../utils/getHashOrDefault';
import AppearanceSettingsView from './appearanceSettingsView';
import { listenToPageDirty, resolvePageDirty } from '../../utils/changeTracking';
import { subscribeTo } from '../../utils/knockout';
import { fire, listenTo } from '../../utils/events';

class FeedbackOverviewView extends RootPage {
    constructor() {
        super();

        this.tabsUseHashes = true;

        this.isDirty = ko.observable();

        IntegrateSlackButton({ activeTab: this.activeTab });
        DisconnectSlackButton({ activeTab: this.activeTab });
        AppearanceSettingsView({ parentPage: this });
        UrlFilterDescriptor(this);

        this.descriptor = ko.observable();

        this.integrations = {
            newFeedbackChannel: ko.observable(),
            newFeedbackWebhook: ko.observable(new NewFeedbackWebhook()),
            newFeedbackEmail: ko.observable(new NewFeedbackEmail())
        };

        let hash = getHashOrDefault('settings').toString();
        if (hash.startsWith('fbd_')) hash = 'feedbacks';

        this.setActiveTab(hash);

        this.selectedLanguage = ko.observable(window.auth.project.SupportedLanguages[0]);
        this.supportedLanguages = languages.filter(i => window.auth.project.SupportedLanguages.indexOf(i.alpha2) !== -1);
    }

    onInit() {
        this.descriptor(new siteConfig.FeedbackDescriptor(this.getServerSideData('descriptor')));

        this.integrations.newFeedbackChannel(this.getServerSideData('slackIntegrationData').newFeedbackChannel);
        this.integrations.newFeedbackWebhook(new NewFeedbackWebhook(this.getServerSideData('newFeedbackWebhookData')));
        this.integrations.newFeedbackEmail(new NewFeedbackEmail(this.getServerSideData('newFeedbackEmailData')));

        $(document).on('change', '.change-track', (e) => {
            if (e.originalEvent) { // this should check the user action?
                this.updatePreview();
            }
        });

        this.integrations.newFeedbackChannel.subscribe(() => {
            this.isDirty(true);
        });

        if (!this.descriptor().recMode()) {
            this.descriptor().recMode('');
        }

        listenToPageDirty(opt => {
            //console.log('DIRTY', opt.fieldName);
            this.isDirty(true);
        });

        listenTo('change-dirty', () => {
            this.isDirty(true);
        });
    }

    saveSettings() {
        return new Promise((resolve) => {
            this.startSubmitting('saveSettings');

            $api.feedbacks.saveSettings(
                { projectCode: auth.project.UniqueCode },
                this.descriptor().toJS(),
                () => {
                    //this.stopSubmitting('saveSettings');
                    //this.showToast('Saved.');
                    resolve();
                },
                () => {
                    reject('settings');
                }
            );
        });
    }

    saveIntegrations() {
        return new Promise((resolve, reject) => {
            $api.integrations.saveFeedbackSettings(
                { projectCode: auth.project.UniqueCode },
                {
                    channel: this.integrations.newFeedbackChannel(),
                    newFeedbackWebhook: this.integrations.newFeedbackWebhook().isEnabled() ? ko.toJS(this.integrations.newFeedbackWebhook()) : null,
                    newFeedbackEmail: this.integrations.newFeedbackEmail().isEnabled() ? ko.toJS(this.integrations.newFeedbackEmail()) : null,
                },
                (resp) => {

                    this.integrations.newFeedbackWebhook().remap(resp.newFeedbackWebhook);
                    this.integrations.newFeedbackEmail().remap(resp.newFeedbackEmail);

                    resolve();
                },
                () => {
                    reject('integrations');
                }
            );
        });
    }

    saveAll() {
        this.startSubmitting('save');

        Promise.all([
            this.saveSettings(),
            this.saveIntegrations()
        ])
            .then(() => {
                this.stopSubmitting('save');
                this.showToast('Saved!');
                this.isDirty(false);
                resolvePageDirty();
            })
            .catch(reason => {
                if (reason == 'settings') {
                    this.showToast('Settings not saved properly.');
                } else if (reason == 'integrations') {
                    this.showToast('Integrations not saved properly.');
                }
                this.stopSubmitting('save');
            })
    }

    updatePreview() {
        /** @type {HTMLIFrameElement} */
        const el = document.getElementById('previewer');

        el.contentWindow.postMessage({
            action: 'update',
            type: 'feedback',
            descriptor: JSON.parse(JSON.stringify(this.descriptor().toJS())),
            language: this.selectedLanguage()
        });
    }

    bindLocalizableProp(observable) {
        return ko.computed({
            read: () => {
                var val = observable();
                if (!val) {
                    return undefined;
                }

                return ko.unwrap(observable()[this.selectedLanguage()]);
            },
            write: (val) => {
                var curV = observable();

                if (!curV) {
                    curV = {};
                }

                curV[this.selectedLanguage()] = val;

                observable(curV);
            }
        });
    }
}

class NewFeedbackWebhook {
    constructor(data = {}) {
        this.method = ko.observable();
        this.url = ko.observable().extend({ urlize: true });
        this.securityToken = ko.observable();
        this.setFeedbackSolved = ko.observable();

        this.isEnabled = ko.observable();

        this.remap(data);

        subscribeTo([this.url, this.setFeedbackSolved], () => {
            fire('change-dirty');
        });
    }

    remap(data) {
        this.isEnabled(!!data);

        if (!data) data = {};

        this.method(data.method);
        this.url(data.url);
        this.securityToken(data.securityToken);
        this.setFeedbackSolved(data.setFeedbackSolved);
    }

    resetToken() {
        if (confirm('Really?')) {
            $api.integrations.resetNewFeedbackWebhookToken(
                { projectCode: auth.project.UniqueCode },
                {},
                (resp) => {
                    this.securityToken(resp.token);
                }
            );
        }
    }

    enable() {
        $api.integrations.enableNewFeedbackWebhook(
            { projectCode: auth.project.UniqueCode },
            {},
            (resp) => {
                this.isEnabled(true);
            }
        );
    }

    disable() {
        $api.integrations.disableNewFeedbackWebhook(
            { projectCode: auth.project.UniqueCode },
            {},
            (resp) => {
                this.isEnabled(false);
            }
        );
    }
}

class NewFeedbackEmail {
    constructor(data = {}) {
        this.email = ko.observable();
        this.setFeedbackSolved = ko.observable();
        this.subject = ko.observable();
        this.uniqueSender = ko.observable();

        this.isEnabled = ko.observable();

        this.remap(data);

        subscribeTo([this.email, this.setFeedbackSolved, this.subject, this.uniqueSender], () => {
            fire('change-dirty');
        });
    }

    remap(data) {
        this.isEnabled(!!data);

        if (!data) data = {};

        this.email(data.email);
        this.setFeedbackSolved(data.setFeedbackSolved);
        this.subject(data.subject);
        this.uniqueSender(data.uniqueSender);
    }


    enable() {
        $api.integrations.enableNewFeedbackEmail(
            { projectCode: auth.project.UniqueCode },
            {},
            (resp) => {
                this.isEnabled(true);
            }
        );
    }

    disable() {
        $api.integrations.disableNewFeedbackEmail(
            { projectCode: auth.project.UniqueCode },
            {},
            (resp) => {
                this.isEnabled(false);
            }
        );
    }
}

export default FeedbackOverviewView;
